import type { ReactNode } from 'react'
import { useState } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import type { ChatParticipant } from './chat-header.interface'
import { ChatHeaderAvatarStack } from './chat-header-avatar-stack.atom'

interface ChatHeaderParticipantListProps {
  participantList: ChatParticipant[]
  onParticipantClick?: (participantId: string) => void
  className?: string
}

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

/**
 * Popover showing list of chat participants with avatar and name.
 * Triggered by the avatar stack button.
 */
export const ChatHeaderParticipantList: React.FC<ChatHeaderParticipantListProps> = ({
  participantList,
  onParticipantClick,
  className,
}): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)

  const handleParticipantClick = (participantId: string): void => {
    onParticipantClick?.(participantId)
    setIsOpen(false)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className={className}>
          <ChatHeaderAvatarStack count={participantList.length} />
        </div>
      </PopoverTrigger>
      <PopoverContent align="end" className="chat-header-participants-popover w-64 p-0">
        <div className="chat-header-participants-header border-b px-3 py-2">
          <h4 className="text-sm font-semibold">Participants ({participantList.length})</h4>
        </div>
        <ScrollArea className={cn(participantList.length > 6 && 'h-[280px]')}>
          <div className="chat-header-participants-list p-1">
            {participantList.map((participant) => (
              <button
                key={participant.id}
                type="button"
                onClick={() => handleParticipantClick(participant.id)}
                className={cn(
                  'chat-header-participant-item flex w-full items-center gap-3 rounded-md px-2 py-2',
                  'hover:bg-accent focus:bg-accent focus:outline-none',
                  'transition-colors'
                )}
              >
                <Avatar className="size-8">
                  <AvatarImage src={participant.avatarUrl} alt={participant.name} />
                  <AvatarFallback className="text-xs">
                    {getInitials(participant.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-sm">{participant.name}</span>
              </button>
            ))}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

ChatHeaderParticipantList.displayName = 'ChatHeaderParticipantList'
